import React, {
  useEffect,
  useState,
  Context,
  createContext,
  useRef,
  useReducer,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { Header } from '@everlywell/leaves';
import { useLDClient } from 'gatsby-plugin-launchdarkly';

import GlobalStyle from 'styles/global';
import { ariesTheme } from 'styles/ariesTheme';
import { ThemeContext, Theme } from 'components/Themable';
import { getUser, resetSession } from 'utils/cartHelpers';
import ewAnalytics from 'utils/analytics';
import { STORE_ROOT, RESULTS_ROOT } from 'utils/constants/urls';
import image from '../../images/logo/everlywell-logo.svg';
import * as S from './styles';
import loadable from '@loadable/component';
import { Spinner } from 'styles/utils';
import { initialState, reducer } from '../../containers/CartContainer/store';
import { setShowTOS } from '../../containers/CartContainer/store/actions';

const Cart = loadable(() => import('../../containers/CartContainer'), {
  fallback: <Spinner />,
});

const Footer = loadable(() => import('../Footer'), {
  fallback: <Spinner />,
});

type Props = {
  children: React.ReactNode;
};

interface UserType {
  userId: number | null;
  loggedIn: boolean;
  isMember: boolean;
  membershipType: string | null;
  activeCredits: number;
  fetched: boolean;
}

interface CartContextType {
  setTOS: Function;
  isOpen: boolean;
}

const defaultUser = {
  userId: null,
  loggedIn: false,
  isMember: false,
  membershipType: null,
  activeCredits: 0,
  fetched: false,
};

export const UserContext: Context<UserType> = createContext<UserType>(
  defaultUser,
);

export const CartContext: Context<CartContextType> = createContext<
  CartContextType
>({ setTOS: () => {}, isOpen: false });

const accountLinks = {
  label: 'Account',
  results: {
    label: 'Test Results',
    url: `${RESULTS_ROOT}/dashboard`,
  },
  settings: {
    label: 'Settings',
    url: `${RESULTS_ROOT}/account/settings`,
  },
  logout: {
    label: 'Log Out',
    url: `${STORE_ROOT}/logout`,
  },
  membership: {
    label: 'My Membership',
  },
};

// backend response still returns `sti` as the membershipType on the user
// we need to map that to its actual name for use with creating links to the page
const membershipNames = {
  sti: 'current',
  control: 'control',
};

const Layout = (props: Props) => {
  const [userData, setUserData] = useState(defaultUser);
  const [cartState, dispatch] = useReducer(reducer, initialState);
  const { isOpen } = cartState;
  const cartProps = { cartState, dispatch };
  const headerRef = useRef<HTMLAnchorElement>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);

  const ldClient = useLDClient();

  useEffect(() => {
    if (window) {
      /* Cart Global Variables */
      // @ts-ignore
      window.appRoot = '';

      /* Just Uno Init */
      // @ts-ignore
      window.ju_num = process.env.JUSTUNO_ACCOUNT_ID; window.asset_host='//cdn.jst.ai/'; (function(i,s,o,g,r,a,m) { i[r] = i[r] || (function() { (i[r].q=i[r].q||[]).push(arguments) }); a = s.createElement(o), m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a,m); })(window,document,'script',asset_host+'vck.js','juapp'); // eslint-disable-line
    }

    const runEffect = async () => {
      try {
        const data = await getUser();
        const sessionData = data ? data.data : {};

        const {
          logged_in: loggedIn,
          is_member: isMember,
          membership_type: membershipType,
          active_credits: activeCredits,
          user_id: userId,
          user_email: userEmail,
        } = sessionData;

        setUserData({
          loggedIn,
          isMember,
          membershipType,
          activeCredits,
          userId,
          fetched: true,
        });

        ldClient?.identify({
          key: 'guest',
          anonymous: true,
        });
      } catch (e) {
        setUserData({
          ...defaultUser,
          fetched: true,
        });

        ldClient?.identify({
          key: 'guest',
          anonymous: true,
        });
      }
    };
    runEffect();
  }, []);

  const setTOS = (isSet: boolean) => {
    setShowTOS(dispatch, isSet);
  };

  const handleTrackableLinkClick = (e: Event) => {
    ewAnalytics.eventHandler(e);
  };

  // links defined in the Header have data-* attributes that get picked up in order to track via Mixpanel
  // the links aren't mounted when the header is closed, so we listen for the header drawer to open, and attach/remove listeners appropriately
  useEffect(() => {
    let trackableLinks: NodeListOf<HTMLAnchorElement> | undefined;

    if (menuOpen || subMenuOpen) {
      trackableLinks = headerRef?.current?.querySelectorAll(
        '[data-analytics-event]',
      );
      if (trackableLinks) {
        trackableLinks.forEach(link =>
          link.addEventListener('click', handleTrackableLinkClick),
        );
      }
    }
    return () => {
      if (trackableLinks) {
        trackableLinks.forEach(link =>
          link.removeEventListener('click', handleTrackableLinkClick),
        );
      }
    };
  }, [menuOpen, subMenuOpen]);

  return (
    <div id="app" className="siteRoot">
      <GlobalStyle />
      <ThemeProvider theme={ariesTheme}>
        <ThemeContext.Provider value={Theme.Default}>
          <UserContext.Provider value={userData}>
            {userData.fetched ? (
              <Header
                loggedIn={userData.loggedIn}
                logoSrc={image}
                logoAlt="Everlywell"
                id="everlywellHeader"
                ref={headerRef}
                cartComponent={<Cart {...cartProps} />}
                accountLinks={accountLinks}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                onSubMenuOpen={() => setSubMenuOpen(true)}
                onSubMenuClose={() => setSubMenuOpen(false)}
                onLogout={() => resetSession()}
                activeMembership={
                  userData.membershipType
                    ? // @ts-ignore
                      membershipNames[userData.membershipType]
                    : undefined
                }
                numOfMemberCredits={userData.activeCredits}
              />
            ) : null}
            <S.Layout>
              <CartContext.Provider value={{ setTOS, isOpen }}>
                {props.children}
              </CartContext.Provider>
            </S.Layout>
            <Footer />
          </UserContext.Provider>
        </ThemeContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export default Layout;
